import type { TwilioErrorRecord } from "@repo/common";
import {
	type CollectionReference,
	collection,
	type DocumentReference,
	doc,
	type Firestore,
	type FirestoreDataConverter,
	getDoc,
	type QueryDocumentSnapshot,
	type SnapshotOptions,
	Timestamp,
} from "firebase/firestore";

export { type TwilioErrorRecord };

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function ensureString(value: unknown): string | undefined {
	if (typeof value === "string" && value.trim().length > 0) {
		return value;
	}
	return undefined;
}

function parseDate(value: unknown): Date {
	if (value instanceof Date) {
		return value;
	}
	if (value instanceof Timestamp) {
		return value.toDate();
	}
	if (typeof value === "number") {
		const millis = value >= 1_000_000_000_000 ? value : value * 1000;
		return new Date(millis);
	}
	if (typeof value === "string") {
		const numericValue = Number(value);
		if (!Number.isNaN(numericValue)) {
			return parseDate(numericValue);
		}
		const parsed = Date.parse(value);
		if (!Number.isNaN(parsed)) {
			return new Date(parsed);
		}
	}
	if (isRecord(value) && typeof value.seconds === "number") {
		const millis =
			value.seconds * 1000 +
			(typeof value.nanoseconds === "number"
				? value.nanoseconds / 1_000_000
				: 0);
		return new Date(millis);
	}
	return new Date(0);
}

function cloneRaw(
	value: Record<string, unknown>,
	id: string,
): Record<string, unknown> {
	try {
		return JSON.parse(JSON.stringify({ ...value, id })) as Record<
			string,
			unknown
		>;
	} catch {
		return { ...value, id };
	}
}

function parsePayload(value: unknown): {
	payload: Record<string, unknown> | undefined;
	payloadText?: string;
} {
	if (isRecord(value)) {
		return { payload: value };
	}

	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			if (isRecord(parsed)) {
				return { payload: parsed, payloadText: value };
			}
			if (Array.isArray(parsed)) {
				return { payload: undefined, payloadText: value };
			}
		} catch {
			// fall through to return payloadText only
		}
		return { payload: undefined, payloadText: value };
	}

	return { payload: undefined };
}

const twilioErrorConverter: FirestoreDataConverter<TwilioErrorRecord> = {
	toFirestore(value) {
		return value.raw ?? {};
	},
	fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions) {
		const data = snapshot.data(options) as Record<string, unknown>;
		const id = ensureString(data.Sid) ?? snapshot.id;
		const level = ensureString(data.Level);
		const occurredAt = parseDate(data.Timestamp);
		const accountSid = ensureString(data.AccountSid);
		const parentAccountSid = ensureString(data.ParentAccountSid);
		const payloadType = ensureString(data.PayloadType);
		const { payload, payloadText } = parsePayload(data.Payload);

		return {
			id,
			level,
			occurredAt,
			accountSid,
			parentAccountSid,
			payloadType,
			payload,
			payloadText,
			raw: cloneRaw(data, id),
		};
	},
};

export function twilioErrorsCollection(
	db: Firestore,
): CollectionReference<TwilioErrorRecord> {
	return collection(db, "twilioErrors").withConverter(twilioErrorConverter);
}

export function twilioErrorDocument(
	db: Firestore,
	errorId: string,
): DocumentReference<TwilioErrorRecord> {
	return doc(db, "twilioErrors", errorId).withConverter(twilioErrorConverter);
}

export async function fetchTwilioError(
	db: Firestore,
	errorId: string,
): Promise<TwilioErrorRecord | null> {
	const snapshot = await getDoc(twilioErrorDocument(db, errorId));
	return snapshot.exists() ? snapshot.data() : null;
}

export function compareTwilioErrorRecords(
	a: TwilioErrorRecord,
	b: TwilioErrorRecord,
): number {
	return b.occurredAt.getTime() - a.occurredAt.getTime();
}

function formatDisplayTimestamp(date: Date): string {
	if (Number.isNaN(date.getTime())) {
		return "Unknown";
	}
	return date.toLocaleString();
}

function payloadString(
	payload: Record<string, unknown> | undefined,
	key: string,
): string | undefined {
	return payload ? ensureString(payload[key]) : undefined;
}

export function toTwilioUiError(record: TwilioErrorRecord) {
	const level = record.level ?? "ERROR";
	const payloadRecord = record.payload;

	const errorCode = payloadString(payloadRecord, "error_code");

	const message =
		payloadString(payloadRecord, "message") ??
		payloadString(payloadRecord, "msg") ??
		record.payloadText ??
		level;

	const phoneNumber = payloadString(payloadRecord, "phone_number");

	const severity = level.toLowerCase() === "warning" ? "warning" : "error";

	return {
		id: record.id,
		timestamp: formatDisplayTimestamp(record.occurredAt),
		level,
		severity,
		errorCode,
		message,
		accountSid: record.accountSid,
		parentAccountSid: record.parentAccountSid,
		payloadType: record.payloadType,
		phoneNumber,
		payload: payloadRecord,
		raw: record.raw,
	};
}

export type TwilioError = ReturnType<typeof toTwilioUiError>;
